import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getPageByPreviewToken } from '@/api/wiki'
import type { WikiPage as WikiPageType } from '@/types/wiki'
import TableOfContents from '@/components/wiki/TableOfContents'
import { transformWikiContentHtml } from '@/utils/transformWikiContentHtml'

export default function WikiPreviewPage() {
    const { token } = useParams<{ token: string }>()
    const [page, setPage] = useState<WikiPageType | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    const rawHtml = page?.content_html ?? ''
    const contentHtml = useMemo(
        () => transformWikiContentHtml(rawHtml),
        [rawHtml],
    )
    const isChangelogPage = rawHtml.includes('changelog-page')
    const isShowcasePage = rawHtml.includes('factory-production-page')
        || rawHtml.includes('dragon-rings-page')

    useEffect(() => {
        if (!token) return
        setLoading(true)
        setError(false)
        getPageByPreviewToken(token)
            .then(setPage)
            .catch(() => setError(true))
            .finally(() => setLoading(false))
    }, [token])

    if (loading) {
        return (
            <div className="panel p-8">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-calamity-panel-hover rounded-lg w-2/3" />
                    <div className="h-4 bg-calamity-panel-hover rounded w-3/4" />
                    <div className="h-4 bg-calamity-panel-hover rounded w-1/2" />
                    <div className="h-4 bg-calamity-panel-hover rounded w-5/6" />
                    <div className="h-4 bg-calamity-panel-hover rounded w-2/3" />
                </div>
            </div>
        )
    }

    if (error || !page) {
        return (
            <div className="panel p-8 text-center">
                <div className="text-5xl mb-4 opacity-50">&#128269;</div>
                <h1 className="text-2xl font-display text-calamity-text mb-2">Draft Not Found</h1>
                <p className="text-calamity-text-dim">
                    This preview link is invalid or has expired.
                </p>
            </div>
        )
    }

    return (
        <div className="flex gap-6">
            <article className={`panel wiki-readable-panel p-6 sm:p-8 flex-1 min-w-0 animate-fade-up relative ${isChangelogPage ? 'wiki-changelog-readable-panel' : ''} ${isShowcasePage ? 'wiki-showcase-readable-panel' : ''}`}>
                {/* Draft Banner */}
                <div className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-calamity-accent/20 text-calamity-accent border border-calamity-accent/30 px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase backdrop-blur-sm z-10 shadow-lg select-none">
                    Draft Preview
                </div>

                {/* Cover image */}
                {page.cover_image_path && (
                    <div className="-mx-6 -mt-6 sm:-mx-8 sm:-mt-8 mb-6 overflow-hidden rounded-t-2xl relative">
                        <div className="absolute inset-0 bg-calamity-bg/20 z-0"></div>
                        <img
                            src={page.cover_image_path}
                            alt={page.title}
                            className="w-full h-48 sm:h-64 object-cover object-center relative z-0 opacity-90 mix-blend-luminosity hover:mix-blend-normal transition-all duration-500"
                        />
                    </div>
                )}

                <div className="wiki-article">
                    {/* Header */}
                    <header className={`wiki-page-header mb-8 ${!page.cover_image_path ? 'mt-4' : ''}`}>
                        <div className="flex items-center gap-3 mb-2 pr-24">
                            {page.icon && <span className="text-4xl filter drop-shadow-sm">{page.icon}</span>}
                            <h1 className="text-3xl sm:text-4xl font-sans font-semibold tracking-tight text-calamity-text leading-tight">
                                {page.title}
                            </h1>
                        </div>
                        {page.meta_description && (
                            <p className="text-base text-calamity-text-dim mt-2">{page.meta_description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-4 text-xs text-calamity-text-muted">
                            <span>Last updated: {new Date(page.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                            {page.category_name && (
                                <span className="px-2.5 py-1 rounded-full bg-calamity-accent/20 border border-calamity-accent/30 text-calamity-text text-[11px] font-semibold">
                                    {page.category_name}
                                </span>
                            )}
                            {!page.published && (
                                <span className="px-2.5 py-1 rounded-full bg-red-500/20 border border-red-500/30 text-red-400 text-[11px] font-semibold">
                                    Unpublished
                                </span>
                            )}
                        </div>
                    </header>

                    {/* Content */}
                    <div
                        className="wiki-content"
                        dangerouslySetInnerHTML={{ __html: contentHtml }}
                    />
                </div>
            </article>

            {/* Table of contents */}
            <TableOfContents contentHtml={page.content_html} />
        </div>
    )
}
